"use client";
import { Checkbox } from "@cmp-src/primitives/checkbox";
import type { ISchedule } from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import { formatScheduleTimeRange } from "@gettactic/helpers/src/date-time/getTimeFromSchedule";
import { format, parseISO } from "date-fns";
import React from "react";
import { getReservationCategoryLabel } from "./categoryLabels";
import type { ReservationDayGroup } from "./groupReservations";

export interface CancelReservationsDayProps {
  group: ReservationDayGroup;
  selectedIds: ReadonlySet<string>;
  /** Ids the signed-in user is not allowed to cancel — rendered disabled. */
  lockedIds: ReadonlySet<string>;
  onToggle: (schedule: ISchedule) => void;
  onToggleMany: (schedules: ISchedule[], selected: boolean) => void;
  parkingVerbiage: string | null;
}

export const CancelReservationsDay: React.FC<CancelReservationsDayProps> = ({
  group,
  selectedIds,
  lockedIds,
  onToggle,
  onToggleMany,
  parkingVerbiage
}) => {
  const selectableSchedules = group.schedules.filter(
    (schedule) => !lockedIds.has(schedule.id)
  );
  const selectedCount = selectableSchedules.filter((schedule) =>
    selectedIds.has(schedule.id)
  ).length;
  const dayState =
    selectedCount === 0
      ? false
      : selectedCount === selectableSchedules.length
        ? true
        : "indeterminate";

  // `group.date` is a plain `yyyy-MM-dd` key — parsed as local midnight so the
  // device timezone cannot shift the label back a day.
  const dayLabel = format(parseISO(`${group.date}T00:00:00`), "EEEE, MMM d");

  return (
    <section className="border-neutral-100 border-b py-3 last:border-b-0">
      <header className="flex items-center gap-2">
        <Checkbox
          id={`cancel-reservations-day-${group.date}`}
          checked={dayState}
          disabled={selectableSchedules.length === 0}
          onCheckedChange={(checked) =>
            onToggleMany(selectableSchedules, checked === true)
          }
        />
        <label
          htmlFor={`cancel-reservations-day-${group.date}`}
          className="cursor-pointer font-semibold text-neutral-900 text-sm"
        >
          {dayLabel}
        </label>
        <span className="text-neutral-500 text-xs">
          {group.schedules.length}
        </span>
      </header>

      {group.categories.map((categoryGroup) => (
        <div key={categoryGroup.category} className="mt-2 pl-6">
          <p className="font-semibold text-[11px] text-neutral-400 uppercase tracking-wide">
            {getReservationCategoryLabel(
              categoryGroup.category,
              parkingVerbiage
            )}
          </p>
          <ul className="mt-1 flex flex-col gap-1">
            {categoryGroup.schedules.map((schedule) => {
              const isLocked = lockedIds.has(schedule.id);
              return (
                <li key={schedule.id} className="flex items-center gap-2">
                  <Checkbox
                    id={`cancel-reservation-${schedule.id}`}
                    checked={selectedIds.has(schedule.id)}
                    disabled={isLocked}
                    onCheckedChange={() => onToggle(schedule)}
                  />
                  <label
                    htmlFor={`cancel-reservation-${schedule.id}`}
                    className={cn(
                      "flex min-w-0 flex-1 items-baseline gap-2 text-sm",
                      isLocked ? "text-neutral-400" : "cursor-pointer"
                    )}
                  >
                    <span className="truncate text-neutral-900">
                      {schedule.resource?.name || schedule.title || "Reserved"}
                    </span>
                    <span className="flex-none text-neutral-500 text-xs">
                      {formatScheduleTimeRange(schedule)}
                    </span>
                    {isLocked && (
                      <span className="flex-none text-neutral-400 text-xs">
                        Can't cancel
                      </span>
                    )}
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </section>
  );
};
